import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';
import type { SectionConfig, TemplateSection } from 'inspection-types';
import {
  type AddSectionToTemplateMutation,
  type AddSectionToTemplateMutationVariables,
  type InspectionSectionConfigInput,
} from 'graphql-query-contracts';
import { addSectionToTemplateMutation } from 'graphql-queries';

import { AddSectionFromTemplateError } from './errors/AddSectionFromTemplateError';

function mapSectionConfigToInput(
  config: SectionConfig | undefined
): InspectionSectionConfigInput | undefined {
  if (!config) {
    return undefined;
  }

  return {
    requirePhotoOnDeficiency: config.requirePhotoOnDeficiency ?? undefined,
    skippable: config.skippable,
    collapsedByDefault: config.collapsedByDefault,
  };
}

export function buildAddSectionToTemplateVariables(params: {
  channelId: string;
  templateId: string;
  section: TemplateSection;
}): AddSectionToTemplateMutationVariables {
  const { channelId, templateId, section } = params;

  return {
    input: {
      channelId,
      templateId,
      sectionId: section.id,
      name: section.name,
      description: section.description ?? undefined,
      order: section.order,
      sectionConfig: mapSectionConfigToInput(section.config),
    },
  };
}

export type AddSectionFromTemplateParams = {
  channelId: string;
  templateId: string;
  section: TemplateSection;
};

export type AddSectionFromTemplateResult = {
  success: boolean;
  newVersion: number;
};

export type UseAddSectionFromTemplateResult = {
  addSectionFromTemplate: (
    params: AddSectionFromTemplateParams
  ) => Promise<AddSectionFromTemplateResult>;
  loading: boolean;
  error: Error | undefined;
};

function validateParams(params: AddSectionFromTemplateParams): void {
  if (!params.channelId?.trim()) {
    throw new AddSectionFromTemplateError('channelId is required');
  }

  if (!params.templateId?.trim()) {
    throw new AddSectionFromTemplateError('templateId is required');
  }

  if (!params.section?.id?.trim()) {
    throw new AddSectionFromTemplateError('section.id is required');
  }

  if (!params.section.name?.trim()) {
    throw new AddSectionFromTemplateError('section.name is required');
  }
}

export const useAddSectionFromTemplate = (): UseAddSectionFromTemplateResult => {
  const [addSection, { loading }] = useMutation<
    AddSectionToTemplateMutation,
    AddSectionToTemplateMutationVariables
  >(addSectionToTemplateMutation);

  const [error, setError] = useState<Error | undefined>(undefined);

  const addSectionFromTemplate = useCallback(
    async (
      params: AddSectionFromTemplateParams
    ): Promise<AddSectionFromTemplateResult> => {
      try {
        setError(undefined);

        validateParams(params);

        const variables = buildAddSectionToTemplateVariables(params);

        const mutationResult = await addSection({ variables });

        const payload = mutationResult.data?.addSectionToTemplate;

        if (!payload) {
          throw new AddSectionFromTemplateError();
        }

        if (!payload.success) {
          throw new AddSectionFromTemplateError(
            'Add section operation did not succeed'
          );
        }

        return {
          success: payload.success,
          newVersion: payload.newVersion,
        };
      } catch (err) {
        const wrappedError =
          err instanceof AddSectionFromTemplateError
            ? err
            : new AddSectionFromTemplateError(
                err instanceof Error
                  ? err.message
                  : 'Failed to add section to inspection template',
                { cause: err }
              );

        setError(wrappedError);

        throw wrappedError;
      }
    },
    [addSection]
  );

  return {
    addSectionFromTemplate,
    loading,
    error,
  };
};
